import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import styled from "@emotion/styled";
import { connect } from "react-redux";

import Abilities from "services/permissions/abilities";
import ContractUtils from "services/contract/utils";
import MapUtils from "services/map/utils";
import ParkStatisticsActioner from "services/parkStatistics/actioner";
import ParkStatisticsUtils from "services/parkStatistics/utils";
import useLiveReload from "services/hooks/useLiveReload";

import ParkkiTheme from "themes/ParkkiTheme";

import DateTimePicker from "components/DateTimePicker";
import Filters from "components/Filters";
import LiveButton from "components/LiveButton";
import SelectedLayersSection from "components/SelectedLayersSection";
import SelectedLayersSectionButton from "components/SelectedLayersSectionButton";

import ParkStatisticsStatsSection from "scenes/ParkStatistics/components/ParkStatisticsStatsSection";

const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: ${ParkkiTheme.colors.lightGrey};
  flex-direction: column;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 8px;
  gap: ${ParkkiTheme.spacings.small};
`;

const FilterContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 200px;
`;

const SelectedAreasContainer = styled.div`
  display: flex;
  justify-self: flex-end;
`;

function ParkStatisticsLayoutComponent({
  contract,
  currentFloorAreas,
  currentFilters,
  onTimestampChange,
  globalFilters,
  parkStatistics,
  setAutoRefresh,
  setFilter,
  setSelectedLayers,
  getAllStats, // Add this prop
}) {
  const [toggleLayersSection, setToggleLayersSection] = useState(false);

  const currentFloorSelectedLayers = useMemo(() => {
    return currentFloorAreas?.filter((area) => area.selected);
  }, [currentFloorAreas]);

  const toggleMarkersAndAreas = useMemo(() => {
    return MapUtils.toggleMarkersAndAreas(
      currentFilters?.timestamp,
      ContractUtils.getParkMaxDaysAllowed(contract)
    );
  }, [contract, currentFilters?.timestamp]);

  const handleDateChange = useCallback(
    (updatedDateTime) => {
      const timestamp = Math.round(updatedDateTime / 1000);
      onTimestampChange(timestamp);
    },
    [onTimestampChange]
  );

  const handleReloadOnce = useCallback(() => {
    handleDateChange(new Date().getTime());
  }, [handleDateChange]);

  const handleSetAutoRefresh = useCallback(() => {
    setAutoRefresh(true);
  }, [setAutoRefresh]);

  const { liveReloadActive, onActivateLiveReload, onCancelLiveReload } =
    useLiveReload({
      key: "PARK_STATS_PAGE_LIVE_RELOAD_INTERVAL_ID",
      onSetAutoRefresh: handleSetAutoRefresh,
      onReload: handleReloadOnce,
    });

  useEffect(() => {
    if (!parkStatistics?.map?.initialized) {
      setToggleLayersSection(false);
    }
  }, [parkStatistics?.map?.initialized]);

  useEffect(() => {
    if (
      !toggleLayersSection &&
      parkStatistics?.map?.initialized &&
      currentFloorSelectedLayers?.length === 0
    ) {
      setToggleLayersSection(true);
    }
  }, [
    toggleLayersSection,
    parkStatistics?.map?.initialized,
    currentFloorSelectedLayers,
  ]);

  const formattedFilters = useMemo(() => {
    return globalFilters.map((filter) => ({
      ...filter,
      selected: currentFilters?.areaType?.indexOf(filter.value) >= 0,
    }));
  }, [currentFilters, globalFilters]);

  function handleAutoReloadChecked(checked) {
    const callback = checked ? onActivateLiveReload : onCancelLiveReload;
    return callback();
  }

  function handleUpdateAreaType(value) {
    setFilter("areaType", value);
  }

  function shouldDisableDate(datetime) {
    const diffDays = moment()
      .startOf("day")
      .diff(datetime.clone().startOf("day"), "days");
    if (
      (diffDays > 0 &&
        diffDays > ContractUtils.getMaxDaysAllowedHeatmapOccupancy(contract)) ||
      diffDays < 0 ||
      (diffDays === 0 && !moment(datetime).isSame(new Date(), "day"))
    ) {
      return true;
    }

    return false;
  }

  return (
    <Container>
      <FiltersContainer>
        <DateTimePicker
          value={currentFilters?.timestamp * 1000}
          onChange={handleDateChange}
          shouldDisableDate={shouldDisableDate}
        />

        <LiveButton
          withLiveReload={liveReloadActive}
          onReload={handleReloadOnce}
          onChecked={handleAutoReloadChecked}
        />

        <FilterContainer>
          <Filters
            filters={formattedFilters}
            setFilters={handleUpdateAreaType}
          />
        </FilterContainer>

        {toggleMarkersAndAreas &&
          Abilities.ability.can("read", "park_stats.areasSelection") && (
            <SelectedAreasContainer>
              <SelectedLayersSectionButton
                toggled={toggleLayersSection}
                onClick={() => setToggleLayersSection(!toggleLayersSection)}
                disabled={getAllStats} // Disable when getAllStats is true
              />
            </SelectedAreasContainer>
          )}
      </FiltersContainer>

      {toggleLayersSection &&
      toggleMarkersAndAreas &&
      Abilities.ability.can("read", "park_stats.areasSelection") &&
      !getAllStats ? ( // Don't show SelectedLayersSection when getAllStats is true
        <SelectedLayersSection
          loading={!parkStatistics?.map?.initialized}
          areas={currentFloorAreas}
          onSelectLayers={setSelectedLayers}
        />
      ) : null}

      <ParkStatisticsStatsSection />
    </Container>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  currentFilters: state.ParkStatistics.filters,
  currentFloorAreas: ParkStatisticsUtils.formatAreasFromCurrentFloor(state),
  globalFilters: state.GlobalFilters.filters,
  parkStatistics: state.ParkStatistics,
});

const mapDispatchToProps = (dispatch) => ({
  setFilter: (key, value) =>
    dispatch(ParkStatisticsActioner.setFilter(key, value)),
  setSelectedLayers: (layers) =>
    dispatch(ParkStatisticsActioner.setSelectedLayers(layers)),
  setAutoRefresh: (autoRefresh) =>
    dispatch(ParkStatisticsActioner.setAutoRefresh(autoRefresh)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ParkStatisticsLayoutComponent);
